import React, { useEffect, useState, useContext, useCallback } from 'react';
import { FaPlus } from 'react-icons/fa';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import Numbers from '~/utils/numbers';
import Dates from '~/utils/dates';
import './styles.css';

// Contexts
import { GeneralContext, ModalContext } from '~/contexts';
import { UnitsContext } from '~/contexts/forms/units';

// Components
import api from '~/services/api';
import AddTeAclModal from './Modals/AddTeAclModal';
import EditTeAclModal from './Modals/EditTeAclModal'; // Fixed incorrect path
import DeleteTeAclModal from './Modals/DeleteTeAclModal'; // Fixed incorrect path

const Form: React.FC<any> = ({ unit, teAcl, setReady, initialYear }) => {
    const [teAclData, setTeAclData] = useState<any>(null);
    const [selectedYear, setSelectedYear] = useState<number>(
        typeof initialYear === 'number' ? initialYear : new Date().getFullYear()
    );
    const [filteredTeAcl, setFilteredTeAcl] = useState<any[]>([]);
    const [autoYearSet, setAutoYearSet] = useState<boolean>(typeof initialYear === 'number');

    // Contexts
    const Units = useContext(UnitsContext);
    const Modal = useContext(ModalContext);
    const {user} = useContext(GeneralContext);

    const unitId = (typeof unit === 'object' && unit !== null) ? unit?.id : unit;

    function formatValueTe (value: string | number) {
        if (typeof value==='undefined') return '';
        else if (typeof value==='string') return Numbers.money(parseFloat(value.replace('.', '').replace(',', '.')), false, true);
        return Numbers.money(value, false, true);
    }

    function getMonthName(month: number) {
        month = Number(month);
        if (month===0) return 'Geral';
        return Dates._getNameMonth((month-1));
    }

    // Inline edit handlers removed in favor of modals

    // Modal handlers - Add new TE/ACL
    function openAddTeModal(defaultMonth?: number) {
        Modal.setModalClass('mteAcl-add');
        Modal.setModalTitle('');
        Modal.setModalBody(
            <AddTeAclModal
                unit={unitId}
                defaultYear={selectedYear}
                defaultMonth={defaultMonth}
                onSave={handleAddTe}
                onCancel={closeSubModal}
            />
        );
        Modal.setModalOpen(true);
    }

    function openEditTeModal(item: any) {
        Modal.setModalClass('mteAcl-edit');
        Modal.setModalTitle('');
        Modal.setModalBody(
            <EditTeAclModal
                item={item}
                onSave={(newPrice: number) => handleEditTe(item, newPrice)}
                onCancel={closeSubModal}
            />
        );
        Modal.setModalOpen(true);
    }

    function openDeleteTeModal(item: any) {
        Modal.setModalClass('mteAcl-delete');
        Modal.setModalTitle('');
        Modal.setModalBody(
            <DeleteTeAclModal
                item={item}
                onConfirm={() => handleDeleteTe(item)}
                onCancel={closeSubModal}
            />
        );
        Modal.setModalOpen(true);
    }

    function closeSubModal() {
        Modal.setModalClass('mteAcl');
        Modal.setModalTitle('Editando TE/ACL');
        Modal.setModalBody(<Form unit={unit} teAcl={teAcl} setReady={setReady} initialYear={selectedYear} />);
        Modal.setModalOpen(true);
    }

    async function handleAddTe(newItem: { year: number; month: number; price: number }) {
        const data = { unit_id: unitId, year: newItem.year, month: newItem.month, value: newItem.price };
        for (const teacl of (Array.isArray(teAclData) ? teAclData : [])) {
            if (Number(teacl.year) === Number(newItem.year) && Number(teacl.month) === Number(newItem.month)) {
                alert('TE/ACL já está cadastrado para este mês. Por favor, selecione um mês diferente.');
                return;
            }
        }
        await Units.saveTeAcl(data);
        await getData();
        closeSubModal();
    }

    async function handleEditTe(original: any, newTeAclValue: number) {
        const data = { unit_id: unitId, year: original.year, month: original.month, value: newTeAclValue };
        const teaclData = (Array.isArray(teAclData) ? teAclData : []).find((t: any) => t.id === original.id);

        if (!teaclData) {
            alert('TE/ACL não encontrado. Por favor, tente novamente.');
            return;
        }

        if (teaclData.year !== original.year || teaclData.month !== original.month) {
            alert('O ano ou mês informado não é o mesmo do TE/ACL. Por favor, tente novamente.');
            return;
        }

        await Units.saveTeAcl(data);
        await getData();
        closeSubModal();
    }

    async function handleDeleteTe(original: any) {
        const payload = { unit_id: unitId, year: original.year, month: original.month, price: original.price, value: original.value, id: original.id };
        await Units.deleteTeAcl(payload);
        await getData();
        closeSubModal();
    }

    const getData = useCallback(async () => {
        if (!unitId) {
            setTeAclData([]);
            return;
        }
        await api.get(`clients/teAcl/${unitId}`, {
            headers: { Authorization: user.token }
        }).then((resp: any) => {
            const raw = resp?.data;
            const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
            const normalized = list.map((item: any) => ({
                ...item,
                // Garantir campo 'price' mesmo que venha como 'value'
                price: typeof item?.price !== 'undefined' ? item.price : (typeof item?.value !== 'undefined' ? item.value : 0),
                year: Number(item?.year ?? 0),
                month: Number(item?.month ?? 0),
            }))
            // Ordenar por ano desc e mês desc
            .sort((a: any, b: any) => {
                const ay = Number(a?.year ?? 0);
                const by = Number(b?.year ?? 0);
                if (ay !== by) return by - ay;
                const am = Number(a?.month ?? 0);
                const bm = Number(b?.month ?? 0);
                return bm - am;
            });
            setTeAclData(normalized);
        }).catch((err: any) => {
            console.error("Erro ao carregar dados do TE ACL:", err);
            setTeAclData([]);
        });
    }, [unitId, user.token]);

    useEffect(() => {
        getData();
    }, [getData, teAcl]);

    function getAvailableYears() {
        const currentYear = new Date().getFullYear();
        const defaultYears: number[] = [];
        for (let i = currentYear; i >= currentYear - 4; i--) defaultYears.push(i);

        if (!Array.isArray(teAclData) || teAclData.length === 0) return defaultYears;

        const dataYears = Array.from(new Set(teAclData.map((item: any) => Number(item.year)))) as number[];
        const allYears = Array.from(new Set([...defaultYears, ...dataYears])) as number[];
        return allYears.sort((a: number, b: number) => b - a);
    }

    function handleYearChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const year = parseInt(event.target.value);
        setSelectedYear(year);
        setAutoYearSet(true);
    }

    const filterByYear = useCallback((year: number) => {
        if (!Array.isArray(teAclData)) return [];
        return teAclData.filter((item: any) => Number(item.year) === Number(year));
    }, [teAclData]);

    function generateFullYearForTeAcl() {
        const allMonths: any[] = [];
        for (let month = 1; month <= 12; month++) {
            const existing = filteredTeAcl.find((item: any) => Number(item.month) === month);
            if (existing) allMonths.push(existing);
            else {
                allMonths.push({
                    id: null,
                    unit_id: unitId,
                    year: selectedYear,
                    month,
                    price: 0,
                    type: 'placeholder'
                });
            }
        }
        return allMonths;
    }

    // Ajustar ano padrão automaticamente (mesma regra do Prices)
    useEffect(() => {
        if (!Array.isArray(teAclData) || teAclData.length === 0) return;
        const years = teAclData.map((item: any) => Number(item.year)).filter((y: any) => Number.isFinite(y));
        if (years.length === 0) return;
        const uniqueYears = Array.from(new Set(years)) as number[];
        const mostRecentYear = Math.max(...uniqueYears);
        const currentYear = new Date().getFullYear();
        const preferredYear = uniqueYears.includes(currentYear) ? currentYear : mostRecentYear;
        if (!autoYearSet && preferredYear !== selectedYear) {
            setSelectedYear(preferredYear);
            setAutoYearSet(true);
        }
    }, [teAclData, autoYearSet, selectedYear]);

    // Atualizar lista filtrada sempre que ano/dados mudarem
    useEffect(() => {
        const filtered = filterByYear(selectedYear);
        const sorted = [...filtered].sort((a: any, b: any) => {
            const ma = Number(a?.month) || 0;
            const mb = Number(b?.month) || 0;
            return mb - ma;
        });
        setFilteredTeAcl(sorted);
    }, [selectedYear, filterByYear]);

    return (
        <div className="teacl-container">
            <div className="teacl-header">
                <h3>TE/ACL</h3>
                <div className="header-controls">
                    <div className="year-filter">
                        <select
                            value={selectedYear}
                            onChange={handleYearChange}
                            className="year-select"
                        >
                            {getAvailableYears().map((year: number) => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="teacl-table">
                <div className="table-header">
                    <div className="col-month">Mês</div>
                    <div className="col-price">Montante (MWh)</div>
                    <div className="col-actions">Ações</div>
                </div>

                <div className="table-body">
                    {generateFullYearForTeAcl().map((el: any) => {
                        const rowKey = el.id || `${el.unit_id}_${el.year}_${el.month}`;
                        return (
                            <div key={rowKey} className="table-row">
                                <div className="col-month" data-label="Mês">
                                    <span>{getMonthName(el.month)}</span>
                                </div>
                                <div className="col-price" data-label="Montante (MWh)">
                                    <span>MWh {formatValueTe(el.price ? el.price : '0.00')}</span>
                                </div>
                                <div className="col-actions" data-label="Ações">
                                    <div className="action-buttons">
                                        {el.id ? (
                                            <>
                                                <button className="btn-edit" onClick={() => openEditTeModal(el)} title="Editar">
                                                    <FiEdit2 size={16} />
                                                </button>
                                                <button className="btn-delete" onClick={() => openDeleteTeModal(el)} title="Deletar">
                                                    <FiTrash2 size={16} />
                                                </button>
                                            </>
                                        ) : (
                                            <button className="btn-edit" onClick={() => openAddTeModal(el.month)} title="Adicionar">
                                                <FaPlus size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default Form;